import React from "react";
import styled from "styled-components";
import { CardComp } from "../cardComp";

const SkeletonBox = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 8px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const DashboardSkeletonLoader = () => {
  return (
    <div className="mx-2 mx-lg-4 animate__animated animate__fadeIn mb-5">
      {/* Welcome Section Skeleton */}
      <section className="row mt-5 mb-4 align-items-center">
        <div className="col-lg-6">
          <SkeletonBox style={{ height: "32px", width: "300px", marginBottom: "12px" }} />
          <SkeletonBox style={{ height: "20px", width: "400px" }} />
        </div>
      </section>

      {/* Cards Skeleton */}
      <section className="row g-4 mb-4">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="col-lg-4">
            <CardComp cardClassName="border-0 pt-3 pb-3">
              <div className="d-flex align-items-center">
                <SkeletonBox
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    marginRight: "16px",
                  }}
                />
                <div className="w-100">
                  <SkeletonBox style={{ height: "28px", width: "120px", marginBottom: "8px" }} />
                  <SkeletonBox style={{ height: "18px", width: "150px" }} />
                </div>
              </div>
            </CardComp>
          </div>
        ))}
      </section>

      {/* Main Content Section */}
      <section className="row">
        {/* Left Column - Statistics and Activities */}
        <div className="col-lg-7">
          {/* Statistics Card Skeleton */}
          <CardComp cardClassName="border-0 pt-3 mb-3">
            <div className="mb-3 d-flex justify-content-between align-items-center px-3">
              <SkeletonBox style={{ height: "24px", width: "120px" }} />
              <SkeletonBox style={{ height: "36px", width: "100px", borderRadius: "6px" }} />
            </div>
            <div className="d-flex gap-4 mb-3 px-3">
              <SkeletonBox style={{ height: "20px", width: "80px" }} />
              <SkeletonBox style={{ height: "20px", width: "80px" }} />
            </div>
            {/* Chart Skeleton */}
            <div style={{ height: "300px", position: "relative", padding: "0 12px" }}>
              <SkeletonBox
                style={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "10px",
                }}
              />
            </div>
          </CardComp>

          {/* Recent Activities Card Skeleton */}
          <CardComp cardClassName="border-0 pt-3 mb-3">
            <div className="mb-3 px-3">
              <SkeletonBox style={{ height: "24px", width: "150px" }} />
            </div>
            <div className="mb-3 px-3">
              <SkeletonBox style={{ height: "40px", width: "100%", borderRadius: "6px" }} />
            </div>
            <div>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-2 mb-3 pb-3"
                  style={{
                    borderBottom: i !== 3 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <SkeletonBox
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                    }}
                  />
                  <div className="flex-grow-1">
                    <SkeletonBox style={{ height: "18px", width: "60%", marginBottom: "8px" }} />
                    <SkeletonBox style={{ height: "16px", width: "40%" }} />
                  </div>
                  <SkeletonBox style={{ height: "24px", width: "80px", borderRadius: "6px" }} />
                </div>
              ))}
            </div>
          </CardComp>
        </div>

        {/* Right Column - SMS Balance, Subscription, Meetings */}
        <div className="col-lg-5">
          {/* SMS Balance Card Skeleton */}
          <CardComp cardClassName="border-0 pt-3 mb-3">
            <div className="px-3">
              <div className="mb-3 pb-3 border-bottom">
                <SkeletonBox style={{ height: "24px", width: "120px" }} />
              </div>
              <div className="text-center pb-2">
                <SkeletonBox style={{ height: "18px", width: "200px", margin: "0 auto 16px" }} />
                <SkeletonBox
                  style={{
                    height: "80px",
                    width: "180px",
                    margin: "0 auto 16px",
                    borderRadius: "12px",
                  }}
                />
                <SkeletonBox style={{ height: "16px", width: "90%", margin: "0 auto 16px" }} />
                <SkeletonBox style={{ height: "16px", width: "70%", margin: "0 auto 24px" }} />
                <SkeletonBox
                  style={{
                    height: "40px",
                    width: "140px",
                    margin: "0 auto",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>
          </CardComp>

          {/* Subscription Card Skeleton */}
          <CardComp cardClassName="border-0 pt-3 mb-3">
            <div className="px-3">
              <div className="mb-3 pb-3 border-bottom d-flex justify-content-between align-items-center">
                <SkeletonBox style={{ height: "24px", width: "120px" }} />
                <SkeletonBox style={{ height: "18px", width: "80px" }} />
              </div>
              <div className="text-center pb-2">
                <SkeletonBox style={{ height: "18px", width: "180px", margin: "0 auto 16px" }} />
                <SkeletonBox
                  style={{
                    height: "80px",
                    width: "180px",
                    margin: "0 auto 16px",
                    borderRadius: "12px",
                  }}
                />
                <SkeletonBox style={{ height: "16px", width: "85%", margin: "0 auto 16px" }} />
                <SkeletonBox
                  style={{
                    height: "40px",
                    width: "120px",
                    margin: "0 auto",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>
          </CardComp>

          {/* Upcoming Meetings Card Skeleton */}
          <CardComp cardClassName="border-0 pt-3 mb-3">
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <SkeletonBox style={{ height: "24px", width: "160px" }} />
              <SkeletonBox style={{ height: "36px", width: "100px", borderRadius: "6px" }} />
            </div>
            <div>
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="mb-4 pb-4"
                  style={{
                    borderBottom: i !== 2 ? "1px solid #f0f0f0" : "none",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <SkeletonBox style={{ height: "20px", width: "70%" }} />
                    <SkeletonBox
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                  <div className="d-flex gap-4 mb-3">
                    <SkeletonBox style={{ height: "40px", width: "120px" }} />
                    <SkeletonBox style={{ height: "40px", width: "120px" }} />
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <SkeletonBox
                      style={{
                        width: "100px",
                        height: "32px",
                        borderRadius: "50%",
                      }}
                    />
                    <SkeletonBox
                      style={{
                        height: "36px",
                        width: "120px",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardComp>
        </div>
      </section>
    </div>
  );
};

export default DashboardSkeletonLoader;

